import { FormEvent, useState } from 'react'

export type FormStatus = 'idle' | 'submitting' | 'success' | 'error'

type Values = Record<string, string>
type Rules<T extends Values> = Partial<Record<keyof T, (value: string) => string | null>>
type Errors<T extends Values> = Partial<Record<keyof T, string>>

export function useLeadForm<T extends Values>(initial: T, rules: Rules<T>) {
  const [values, setValues] = useState<T>(initial)
  const [errors, setErrors] = useState<Errors<T>>({})
  const [status, setStatus] = useState<FormStatus>('idle')

  const setField = (name: keyof T, value: string) => {
    setValues((current) => ({ ...current, [name]: value }))
    if (errors[name]) setErrors((current) => ({ ...current, [name]: undefined }))
  }

  const validate = () => {
    const next: Errors<T> = {}
    ;(Object.keys(rules) as (keyof T)[]).forEach((key) => {
      const message = rules[key]?.(values[key] ?? '')
      if (message) next[key] = message
    })
    setErrors(next)
    return Object.keys(next).length === 0
  }

  // Replace the timeout with the real submission endpoint + Turnstile token verification.
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (status === 'submitting') return
    if (!validate()) {
      setStatus('error')
      return
    }
    setStatus('submitting')
    await new Promise((resolve) => setTimeout(resolve, 900))
    setStatus('success')
  }

  const reset = () => {
    setValues(initial)
    setErrors({})
    setStatus('idle')
  }

  return { values, errors, status, setField, setValues, submit, reset }
}
